import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { notificationsService } from '@/services/notifications'
import type { NotificationFilters, CreateNotificationData, Notification } from '@/types/notification'

// STORY-033: Hook pour gerer les notifications

/**
 * Hook principal pour la liste des notifications
 */
export function useNotifications(filters?: NotificationFilters) {
  const queryClient = useQueryClient()

  const notificationsQuery = useQuery({
    queryKey: ['notifications', filters],
    queryFn: () => notificationsService.getAll(filters),
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateNotificationData) => notificationsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-stats'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => notificationsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-stats'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    },
  })

  return {
    notifications: notificationsQuery.data ?? [],
    isLoading: notificationsQuery.isLoading,
    error: notificationsQuery.error,
    refetch: notificationsQuery.refetch,
    createNotification: createMutation.mutateAsync,
    deleteNotification: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}

/**
 * Hook pour les notifications non lues
 */
export function useUnreadNotifications(limit = 10) {
  return useQuery({
    queryKey: ['notifications', 'unread', limit],
    queryFn: () => notificationsService.getUnread(limit),
    refetchInterval: 30000,
  })
}

/**
 * Hook pour compter les notifications non lues
 */
export function useUnreadNotificationsCount() {
  return useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => notificationsService.countUnread(),
    refetchInterval: 30000,
  })
}

/**
 * Hook pour les statistiques des notifications
 */
export function useNotificationsStats() {
  return useQuery({
    queryKey: ['notifications-stats'],
    queryFn: () => notificationsService.getStats(),
  })
}

/**
 * Hook pour marquer une notification comme lue
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => notificationsService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-stats'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    },
  })
}

/**
 * Hook pour marquer toutes les notifications comme lues
 */
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-stats'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    },
  })
}

/**
 * Hook pour s'abonner aux notifications en temps reel
 */
export function useNotificationsSubscription(
  onNewNotification: (notification: Notification) => void
) {
  const queryClient = useQueryClient()

  useEffect(() => {
    const unsubscribe = notificationsService.subscribeToNotifications((notification) => {
      onNewNotification(notification)
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-stats'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    })

    return () => {
      unsubscribe()
    }
  }, [onNewNotification, queryClient])
}

/**
 * Hook combine pour le header (compteur + dropdown)
 */
export function useHeaderNotifications() {
  const { data: count = 0, isLoading: countLoading } = useUnreadNotificationsCount()
  const { data: notifications = [], isLoading: notificationsLoading } = useUnreadNotifications(5)
  const markAsRead = useMarkNotificationAsRead()
  const markAllAsRead = useMarkAllNotificationsAsRead()

  return {
    count,
    notifications,
    isLoading: countLoading || notificationsLoading,
    markAsRead: markAsRead.mutateAsync,
    markAllAsRead: markAllAsRead.mutateAsync,
    isMarkingAsRead: markAsRead.isPending,
    isMarkingAllAsRead: markAllAsRead.isPending,
  }
}
